import type { AlertView } from '@occ/contracts';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeMouseHandler,
  type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Loading } from '../components/ui';
import { fetchAlerts, groupAlertsByNode } from '../data/alerts';
import { fetchNodes } from '../data/nodes';
import { useHierarchy } from '../hierarchy/HierarchyProvider';
import { BranchHoverCard } from '../topology/BranchHoverCard';
import {
  branchNodeId,
  buildGraph,
  clientNodeId,
  leafNodeId,
  type TopologyNodeData,
} from '../topology/buildGraph';
import { ClientHoverCard } from '../topology/ClientHoverCard';
import { TOPOLOGY_COLOR_HEX } from '../topology/color';
import { TOPOLOGY_NODE_HEIGHT, TOPOLOGY_NODE_WIDTH } from '../topology/layout';
import { LeafActionsPopover, type LeafActionsPopoverData } from '../topology/LeafActionsPopover';
import { LeafHoverCard } from '../topology/LeafHoverCard';
import { findTopologyMatch } from '../topology/search';
import { topologyNodeTypes } from '../topology/TopologyNodes';

type HoverState = { id: string; x: number; y: number };
type PopoverState = { nodeId: string; x: number; y: number };

/** Cada cuánto se refresca `GET /alerts?status=firing` (sección 5: REST, no WebSocket). */
const ALERTS_POLL_MS = 45_000;

/** Cuánto dura el resalte del buscador (sección 9) antes de apagarse solo. */
const SEARCH_HIGHLIGHT_MS = 2_500;

/**
 * Vista de mapa (Módulo 8, spec M8-visualizador-topologico).
 *
 * Tercera forma de ver la jerarquía Cliente→Sucursal→Nodo, para reconocimiento
 * visual rápido en vez de administración (`HierarchyAdminPage`) o búsqueda
 * puntual (`NodesPage`) — no reemplaza a ninguna de las dos.
 *
 * Fase 3 (secciones 3, 5, 6, 7.1/7.2/7.3 del spec): integra `GET /alerts` con
 * sondeo periódico, colorea por severidad agregada y completa el hover de las
 * tres cajas (hoja/sucursal/cliente).
 *
 * Fase 4 (sección 8.2): clic en un nodo hoja abre el popover de acciones
 * rápidas en vez de navegar directo — la ficha completa queda como una de sus
 * tres opciones, no como el único destino.
 *
 * Fase 5 (sección 9): buscador por alias/sucursal/cliente que expande lo
 * necesario y centra+resalta el resultado; colapso manual de sucursal
 * (independiente del colapso de cliente); minimap de React Flow.
 */
export function TopologyPage() {
  const { tree, loading, healthByNode } = useHierarchy();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [expandedClientIds, setExpandedClientIds] = useState<Set<string>>(
    () => new Set(routeClientId ? [routeClientId] : []),
  );
  // Colapso manual de sucursal (sección 9), independiente del de cliente:
  // vacío por omisión, toda sucursal de un cliente expandido arranca expandida.
  const [collapsedBranchIds, setCollapsedBranchIds] = useState<Set<string>>(() => new Set());

  // Entrar directo a `/topology/:clientId` expande ese cliente sin tocar los
  // demás que ya estuvieran expandidos.
  useEffect(() => {
    if (!routeClientId) return;
    setExpandedClientIds((current) =>
      current.has(routeClientId) ? current : new Set(current).add(routeClientId),
    );
  }, [routeClientId]);

  // `lastSeenAt` no viaja en el árbol vivo de `HierarchyProvider` (solo trae
  // `status`); se pide una vez a `GET /nodes`, que ya lo expone, en vez de
  // sumar un endpoint nuevo solo para el hover.
  const [lastSeenById, setLastSeenById] = useState<Record<string, Date | null>>({});
  useEffect(() => {
    let cancelled = false;
    fetchNodes()
      .then((rows) => {
        if (cancelled) return;
        setLastSeenById(Object.fromEntries(rows.map((row) => [row.id, row.lastSeenAt])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Alertas abiertas, por sondeo: `listAlertsQuery` no filtra por cliente ni
  // sucursal, así que se piden todas de una vez y se agrupan aquí.
  const [alerts, setAlerts] = useState<AlertView[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchAlerts({ status: 'firing' })
        .then((rows) => {
          if (!cancelled) setAlerts(rows);
        })
        .catch(() => undefined);
    };
    load();
    const interval = setInterval(load, ALERTS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const alertsByNode = useMemo(() => groupAlertsByNode(alerts), [alerts]);

  const { nodes, edges } = useMemo<{ nodes: Node<TopologyNodeData>[]; edges: Edge[] }>(
    () =>
      tree
        ? buildGraph(tree, expandedClientIds, alertsByNode, collapsedBranchIds)
        : { nodes: [], edges: [] },
    [tree, expandedClientIds, alertsByNode, collapsedBranchIds],
  );

  const [hover, setHover] = useState<HoverState | null>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);

  const onNodeClick = useCallback<NodeMouseHandler>((event, node) => {
    const data = node.data as TopologyNodeData;
    if (data.kind === 'client') {
      setExpandedClientIds((current) => {
        const next = new Set(current);
        if (next.has(data.clientId)) next.delete(data.clientId);
        else next.add(data.clientId);
        return next;
      });
      return;
    }
    if (data.kind === 'branch') {
      setCollapsedBranchIds((current) => {
        const next = new Set(current);
        if (next.has(data.branchId)) next.delete(data.branchId);
        else next.add(data.branchId);
        return next;
      });
      return;
    }
    if (data.kind === 'leaf') {
      setPopover((current) => {
        if (current?.nodeId === data.nodeId) return null;
        const rect = wrapperRef.current?.getBoundingClientRect();
        return {
          nodeId: data.nodeId,
          x: rect ? event.clientX - rect.left : 0,
          y: rect ? event.clientY - rect.top : 0,
        };
      });
    }
  }, []);

  const onNodeMouseEnter = useCallback<NodeMouseHandler>((event, node) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({ id: node.id, x: event.clientX - rect.left, y: event.clientY - rect.top });
  }, []);

  const onNodeMouseLeave = useCallback(() => setHover(null), []);

  const hoveredNode = useMemo(
    () => (hover ? nodes.find((candidate) => candidate.id === hover.id) : undefined),
    [hover, nodes],
  );

  const hoveredLeaf = useMemo(() => {
    if (!hoveredNode || hoveredNode.data.kind !== 'leaf') return null;
    const data = hoveredNode.data;
    const client = tree?.clients.find((candidate) => candidate.id === data.clientId);
    const branch = client?.branches.find((candidate) => candidate.id === data.branchId);
    return {
      alias: data.alias,
      status: data.status,
      lastSeenAt: lastSeenById[data.nodeId] ?? null,
      breadcrumb: client && branch ? `${client.name} · ${branch.name}` : null,
      alert: data.alert,
      health: healthByNode[data.nodeId] ?? null,
    };
  }, [hoveredNode, lastSeenById, tree, healthByNode]);

  // El nodo puede desaparecer del árbol (se colapsó su cliente) mientras el
  // popover seguía abierto; `find` devuelve `undefined` y con él se cierra solo.
  const popoverLeaf = useMemo<LeafActionsPopoverData | null>(() => {
    if (!popover) return null;
    const node = nodes.find((candidate) => candidate.id === leafNodeId(popover.nodeId));
    if (!node || node.data.kind !== 'leaf') return null;
    const data = node.data;
    const client = tree?.clients.find((candidate) => candidate.id === data.clientId);
    if (!client) return null;
    return {
      nodeId: data.nodeId,
      alias: data.alias,
      status: data.status,
      path: {
        tenantId: client.tenantId,
        clientId: data.clientId,
        branchId: data.branchId,
        nodeId: data.nodeId,
      },
    };
  }, [popover, nodes, tree]);

  // Buscador (sección 9): expande cliente/sucursal lo necesario para que el
  // resultado sea visible y lo marca para centrar+resaltar. `nodes` recalcula
  // en el mismo commit (los `setState` de expansión se baten juntos), así que
  // el efecto de centrado de más abajo ya lo encuentra en la siguiente pasada.
  const [searchTerm, setSearchTerm] = useState('');
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

  const handleSearch = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (!tree) return;
      const match = findTopologyMatch(tree, searchTerm);
      if (!match) {
        setSearchNotice(searchTerm.trim() ? 'Sin resultados' : null);
        setHighlightId(null);
        return;
      }
      setSearchNotice(null);
      setExpandedClientIds((current) =>
        current.has(match.clientId) ? current : new Set(current).add(match.clientId),
      );
      if (match.kind === 'leaf') {
        setCollapsedBranchIds((current) => {
          if (!current.has(match.branchId)) return current;
          const next = new Set(current);
          next.delete(match.branchId);
          return next;
        });
      }
      setHighlightId(
        match.kind === 'client'
          ? clientNodeId(match.clientId)
          : match.kind === 'branch'
            ? branchNodeId(match.branchId)
            : leafNodeId(match.nodeId),
      );
    },
    [tree, searchTerm],
  );

  // El resalte se apaga solo: no queda pegado si el usuario ya siguió de largo.
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), SEARCH_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // Centra el viewport sobre el resultado. Depende de `nodes` porque recién ahí
  // aparece con posición real (dagre recalculó el layout tras expandir).
  useEffect(() => {
    if (!highlightId) return;
    const target = nodes.find((candidate) => candidate.id === highlightId);
    const instance = reactFlowInstanceRef.current;
    if (!target || !instance) return;
    instance.setCenter(
      target.position.x + TOPOLOGY_NODE_WIDTH / 2,
      target.position.y + TOPOLOGY_NODE_HEIGHT / 2,
      { zoom: 1.2, duration: 500 },
    );
  }, [nodes, highlightId]);

  const nodesForFlow = useMemo(
    () =>
      highlightId
        ? nodes.map((node) =>
            node.id === highlightId
              ? { ...node, className: 'ring-4 ring-blue-500 ring-offset-2 rounded-lg' }
              : node,
          )
        : nodes,
    [nodes, highlightId],
  );

  const nodeColorForMiniMap = useCallback(
    (node: Node) => TOPOLOGY_COLOR_HEX[(node.data as TopologyNodeData).color],
    [],
  );

  if (loading && !tree) return <Loading label="Cargando topología…" />;

  return (
    <section className="grid h-[calc(100vh-6.5rem)] grid-rows-[auto_1fr] gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Vista de mapa</h1>
          <p className="text-sm opacity-60">
            Clic en un cliente o sucursal para expandirlo/colapsarlo, en un nodo para sus acciones
            rápidas. Rueda del mouse para zoom, arrastre para desplazar.
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            aria-label="Buscar cliente, sucursal o nodo"
            placeholder="Buscar alias, sucursal o cliente…"
            className="w-64 rounded-md border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/20"
          />
          <button
            type="submit"
            className="rounded-md border border-black/15 px-3 py-1 text-sm dark:border-white/20"
          >
            Buscar
          </button>
          {searchNotice && <span className="text-sm opacity-60">{searchNotice}</span>}
        </form>
      </div>

      <div
        ref={wrapperRef}
        className="relative min-h-0 rounded-lg border border-black/10 dark:border-white/10"
      >
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodesForFlow}
            edges={edges}
            nodeTypes={topologyNodeTypes}
            onNodeClick={onNodeClick}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            onPaneClick={() => setPopover(null)}
            onInit={(instance) => {
              reactFlowInstanceRef.current = instance;
            }}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={nodeColorForMiniMap} pannable zoomable />
          </ReactFlow>
        </ReactFlowProvider>

        {/* El popover de acciones gana: mientras está abierto se calla el hover, que si no
            duplicaría alias y estado del mismo nodo en dos tarjetas superpuestas. */}
        {!popover && hover && hoveredNode?.data.kind === 'client' && (
          <ClientHoverCard data={hoveredNode.data} x={hover.x} y={hover.y} />
        )}
        {!popover && hover && hoveredNode?.data.kind === 'branch' && (
          <BranchHoverCard data={hoveredNode.data} x={hover.x} y={hover.y} />
        )}
        {!popover && hover && hoveredNode?.data.kind === 'leaf' && hoveredLeaf && (
          <LeafHoverCard data={hoveredLeaf} x={hover.x} y={hover.y} />
        )}
        {popover && popoverLeaf && (
          <LeafActionsPopover
            data={popoverLeaf}
            x={popover.x}
            y={popover.y}
            onClose={() => setPopover(null)}
            onNavigate={(to) => {
              setPopover(null);
              navigate(to);
            }}
          />
        )}
      </div>
    </section>
  );
}
